<template>
  <section class="completion-recommendations">
    <h2 class="completion-recommendations__title">What's next?</h2>
    <div class="completion-recommendations__list">
      <article
        v-for="(recommendation, index) in recommendations"
        :key="recommendation.book.id"
        v-on-visible.once="() => emit('impression', recommendation, index + 1)"
        class="completion-recommendations__item"
      >
        <button
          class="completion-recommendations__card"
          type="button"
          @click="emit('select', recommendation, index + 1)"
        >
          <div class="completion-recommendations__summary">
            <BookCardThumbnail
              class="completion-recommendations__thumbnail"
              :doc="{
                title: recommendation.book.title,
                author: recommendation.book.author,
                cover_image: recommendation.book.cover_image ?? undefined,
              }"
            >
              <div class="completion-recommendations__badges">
                <BookListenIcon v-if="recommendation.book.has_audio" />
                <FreeBookIcon v-if="recommendation.book.is_free" />
              </div>
            </BookCardThumbnail>

            <div class="completion-recommendations__details">
              <p class="completion-recommendations__category">
                {{ recommendation.book.tags[0] }}
              </p>
              <h3 class="completion-recommendations__book-title">
                {{ recommendation.book.title }}
              </h3>
              <p class="completion-recommendations__author">
                {{ recommendation.book.author }}
              </p>
              <TimeEstimate
                v-if="recommendation.book.summary_words"
                :doc="recommendation.book"
              />
            </div>
          </div>

          <p class="completion-recommendations__pitch">
            {{ recommendation.sales_pitch }}
          </p>
        </button>

        <button
          class="completion-recommendations__favorite"
          type="button"
          :aria-label="
            isFavorite(recommendation.book.id)
              ? `Remove ${recommendation.book.title} from Favorites`
              : `Add ${recommendation.book.title} to Favorites`
          "
          :disabled="favoriteRequestId !== null"
          @click="toggleFavorite(recommendation, index + 1)"
        >
          <span
            class="iconfont"
            :class="isFavorite(recommendation.book.id) ? 'iflag2' : 'iflag'"
          ></span>
        </button>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import BookListenIcon from '@/app/components/book/card/BookListenIcon.vue'
import FreeBookIcon from '@/app/components/book/card/FreeBookIcon.vue'
import TimeEstimate from '@/app/components/doc/TimeEstimate.vue'
import type { CompletionRecommendationData } from '@/models/completion'
import { backend } from '@/services/backend'
import { vOnVisible } from '@/helpers/directives'
import {
  DOCS_USER_DATA_CACHE,
  HOME_CACHE,
  resetCacheByKeys,
} from '@/helpers/cache'

const props = defineProps<{
  recommendations: CompletionRecommendationData[]
}>()

const emit = defineEmits<{
  (
    event: 'select',
    recommendation: CompletionRecommendationData,
    position: number,
  ): void
  (
    event: 'favorite',
    recommendation: CompletionRecommendationData,
    position: number,
    action: 'favorite-recommendation' | 'unfavorite-recommendation',
  ): void
  (
    event: 'impression',
    recommendation: CompletionRecommendationData,
    position: number,
  ): void
}>()

const favoriteIds = ref(
  new Set(
    props.recommendations
      .filter((recommendation) => recommendation.is_favorite)
      .map((recommendation) => recommendation.book.id),
  ),
)
const favoriteRequestId = ref<string | null>(null)

function isFavorite(bookId: string): boolean {
  return favoriteIds.value.has(bookId)
}

async function toggleFavorite(
  recommendation: CompletionRecommendationData,
  position: number,
): Promise<void> {
  if (favoriteRequestId.value !== null) {
    return
  }

  const book = recommendation.book
  favoriteRequestId.value = book.id
  emit(
    'favorite',
    recommendation,
    position,
    isFavorite(book.id)
      ? 'unfavorite-recommendation'
      : 'favorite-recommendation',
  )

  try {
    const favoriteData = {
      id: book.id,
      title: book.title,
      doc_type: 'book' as const,
      is_free: book.is_free,
      summary_words: book.summary_words,
    }
    const result = isFavorite(book.id)
      ? await backend.unfavorite(favoriteData)
      : await backend.favorite(favoriteData)

    const updatedFavoriteIds = new Set(favoriteIds.value)
    if (result.is_favorite) {
      updatedFavoriteIds.add(book.id)
    } else {
      updatedFavoriteIds.delete(book.id)
    }
    favoriteIds.value = updatedFavoriteIds

    // Other screens read favorite state from these caches, while this popup
    // keeps its own state. Clear them so those screens load the updated value.
    await resetCacheByKeys([HOME_CACHE, DOCS_USER_DATA_CACHE])
  } finally {
    favoriteRequestId.value = null
  }
}
</script>

<style lang="scss" scoped>
.completion-recommendations {
  width: 100%;

  &__title {
    margin: 0 0 20px;
    color: $c-black-2;
    font-size: 24px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: -0.02em;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__item {
    position: relative;
    z-index: 0;
    overflow: hidden;
    border-radius: 12px;
  }

  &__card {
    display: block;
    width: 100%;
    padding: 0;
    color: inherit;
    text-align: left;
  }

  &__summary {
    position: relative;
    z-index: 1;
    display: flex;
    height: 170px;
    overflow: hidden;
    border: 1px solid $c-border-7;
    border-radius: 8px;
    background: $c-white;
  }

  &__thumbnail {
    display: grid;
    flex: 0 0 122px;
    width: 122px;
    height: 100%;
    place-items: center;
    background: #fffde9;

    :deep(.book-thumbnail__cover:not(.book-thumbnail__cover--background)) {
      width: 82px;
      height: 110px;
      overflow: hidden;
      border-radius: 4px;
      filter: drop-shadow(0 2px 8px rgba($c-black-1, 0.11));
    }

    :deep(.book-cover__image),
    :deep(.fallback-cover) {
      width: 100%;
      height: 100%;
    }

    :deep(.book-cover__image) {
      object-fit: cover;
    }
  }

  &__badges {
    position: absolute;
    z-index: 2;
    top: 5px;
    left: 5px;
    display: flex;
    gap: 4px;

    :deep(.books__play) {
      display: grid;
      width: 24px;
      height: 24px;
      place-items: center;
      border-radius: 50%;
      background: $c-white;
      box-shadow: 0 2px 6px rgba($c-black-2, 0.1);
    }

    :deep(.books__play-icon) {
      font-size: 18px;
      line-height: 14px;
    }

    :deep(.books__free-icon) {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px 9px 2px;
      border: 1px solid $c-white;
      border-radius: 8px;
      background: $c-blue-4;
      box-shadow: 0 2px 6px rgba($c-black-2, 0.1);
      color: $c-white;
      font-size: 10px;
      font-weight: 600;
      line-height: 16px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
  }

  &__details {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    padding: 28px 48px 20px 16px;
  }

  &__category {
    margin: 0 0 8px;
    color: $c-black-6;
    font-size: 10px;
    line-height: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  &__book-title {
    margin: 0;
    color: $c-black-2;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
  }

  &__author {
    margin: 0;
    color: $c-black-3;
    font-size: 12px;
    line-height: 18px;
  }

  :deep(.time-estimate) {
    min-height: 24px;
    padding: 2px 6px 2px 4px;
    margin-top: auto;
    background: $c-bg;
  }

  &__pitch {
    min-height: 111px;
    padding: 28px 20px 20px;
    border-radius: 0 0 12px 12px;
    margin: -8px 0 0;
    background: $c-bg;
    color: $c-black-3;
    font-size: 16px;
    line-height: 1.3;
  }

  &__favorite {
    position: absolute;
    z-index: 2;
    top: 20px;
    right: 16px;
    display: grid;
    width: 24px;
    height: 24px;
    place-items: center;
    color: $c-black-6;
    font-size: 20px;

    &:disabled {
      cursor: default;
    }

    .iflag2 {
      color: $c-black-2;
    }
  }
}

.night-mode .completion-recommendations {
  &__title,
  &__book-title {
    color: $c-dark-4;
  }

  &__summary {
    border-color: $c-dark-3;
    background: $c-dark-1;
  }

  &__category,
  &__author,
  &__pitch {
    color: $c-dark-8;
  }

  :deep(.time-estimate) {
    border-color: $c-dark-8;
    background: $c-dark-1;
    color: $c-dark-8;
  }

  &__pitch {
    background: $c-dark-2;
  }

  &__favorite {
    color: $c-dark-4;

    .iflag2 {
      color: $c-yellow;
    }
  }

  &__badges {
    :deep(.books__play) {
      background: $c-dark-2;
    }

    :deep(.books__free-icon) {
      border-color: $c-dark-4;
      color: $c-dark-4;
    }
  }
}
</style>
